import { TslDebug } from '@/components/TslDebug';
import TslDebugArea from '@/components/TslDebugArea';
import WebGpuCanvas from '@/components/WebGpuCanvas';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import * as React from 'react';
import {
  Fn,
  positionLocal,
  length,
  abs,
  smoothstep,
  vec2,
  time,
  cos,
  sin,
  mix,
  vec3,
  step,
  negate,
  mod,
} from 'three/tsl';
import { JoinNode } from 'three/webgpu';

export type Props = {};

const Circle = Fn(([p, r, t]: [p: typeof positionLocal, radius: number, thickness: number]) => {
  const distance = length(p).sub(r);
  return smoothstep(t, 0.0, abs(distance));
});

const Line = Fn(
  ([p, dir, distance, t]: [p: typeof positionLocal, direction: JoinNode, distance: number, thickness: number]) => {
    const projected = p.dot(dir);
    const clampledProjection = projected.clamp(0.0, distance);
    // return clampledProjection;
    // return mix(
    //   smoothstep(t, 0.0, length(projected.mul(dir))),
    //   vec3(1, 0, 0),
    //   smoothstep(t, 0.0, length(p.sub(clampledProjection.mul(dir))))
    // );
    return smoothstep(t, 0.0, length(p.sub(clampledProjection.mul(dir))));
  }
);

function App04(props: Props) {
  const {} = props;
  const fragmentNode = React.useMemo(
    () =>
      Fn(() => {
        const p = positionLocal;
        const radius = 0.1;
        const thickness = 0.01;
        const circleOffset = vec2(-0.33, 0.33);
        const circleCenter = p.sub(circleOffset);
        const circle = Circle(circleCenter, radius, thickness);

        const angle = time;
        const direction = vec2(cos(angle), sin(angle));
        const radiusLine = Line(circleCenter, direction, radius, thickness);
        const radiusEndPosition = direction.mul(radius);
        const frequency = Math.PI * 2;
        const sinWave = smoothstep(
          thickness,
          0.0,
          abs(p.y.sub(circleOffset.y).sub(sin(p.x.mul(frequency).sub(time).sub(Math.PI)).mul(radius)))
        ); // offset it
        sinWave.assign(step(0, p.x).mul(sinWave)); // draw only part of it right of p.x = 0
        const cosWave = smoothstep(
          thickness,
          0,
          abs(p.x.sub(circleOffset.x).sub(cos(p.y.mul(frequency).add(time)).mul(radius)))
        );
        cosWave.assign(step(p.y, 0).mul(cosWave)); // draw only part of it above p.y = 0

        const sineReferenceLine = Line(
          p.sub(circleOffset).sub(radiusEndPosition),
          vec2(1, 0),
          negate(radiusEndPosition.x).sub(circleOffset.x),
          thickness
        );
        const cosineReferenceLine = Line(
          p.sub(circleOffset).sub(radiusEndPosition),
          vec2(0, -1),
          radiusEndPosition.y.add(circleOffset.y),
          thickness
        );
        const dottedSineReferenceLine = sineReferenceLine.mul(step(0.5, mod(p.x.mul(10), 1)));
        const dottedCosineReferenceLine = cosineReferenceLine.mul(step(0.5, mod(p.y.mul(10), 1)));

        const finalColor = mix(circle, vec3(1, 0, 0), radiusLine);
        finalColor.assign(mix(finalColor, vec3(0, 1, 0), sinWave));
        finalColor.assign(mix(finalColor, vec3(0, 1, 0), cosWave));
        finalColor.assign(mix(finalColor, vec3(1, 1, 0), dottedSineReferenceLine));
        finalColor.assign(mix(finalColor, vec3(1, 1, 0), dottedCosineReferenceLine));
        return finalColor;
      }),
    []
  );
  return (
    <TslDebugArea style={{ backgroundColor: 'black' }}>
      {(el) => (
        <WebGpuCanvas>
          <PerspectiveCamera
            makeDefault
            fov={53}
            aspect={el.clientWidth / el.clientHeight}
            near={0.1}
            far={10}
            position={[0, 0, 1]}
          />
          <mesh name="node-mesh">
            <planeGeometry args={[5, 5]} />
            <nodeMaterial fragmentNode={fragmentNode()} />
            <TslDebug meshName={'node-mesh'} />
          </mesh>
          <gridHelper rotation={[-Math.PI / 2, 0, 0]} />
          <axesHelper />
          <OrbitControls />
        </WebGpuCanvas>
      )}
    </TslDebugArea>
  );
}

export default React.memo(App04);
